/**
 * Página dedicada do Pronto Atendimento (fora da agenda).
 * Toggle local profissional / clínica — não altera ViewContext global.
 * Visual do seletor alinhado ao ProfessionalDashboard (Tabs centralizados).
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  AdminPanelSettings,
  MedicalServices,
  Person,
} from "@mui/icons-material";
import { LargeContentBox } from "@/components/common";
import { PageHeader } from "@/components/common/PageHeader";
import { useViewContext } from "@/contexts/ViewContext";
import { useProntoAtendimentoContractContext } from "@/contexts/ProntoAtendimentoContractContext";
import ScheduleProntoAtendimentoPanel from "@/components/schedule/prontoAtendimento/ScheduleProntoAtendimentoPanel";
import ScheduleProntoAtendimentoClinicPanel from "@/components/schedule/prontoAtendimento/ScheduleProntoAtendimentoClinicPanel";

type PaViewMode = "professional" | "clinic";

/** Tabs locais (não usam ViewContext) — mesmo padrão visual do dashboard. */
function PaViewTabs({
  value,
  onChange,
}: {
  value: PaViewMode;
  onChange: (value: PaViewMode) => void;
}) {
  return (
    <Paper elevation={0} sx={{ borderRadius: 1, overflow: "hidden" }}>
      <Grid
        container
        spacing={2}
        sx={{ pb: 1.5, px: 2 }}
        alignItems="center"
      >
        {/* Espaço à esquerda — espelha o Grid 3 colunas do dashboard */}
        <Grid item xs={false} sm={4} sx={{ display: { xs: "none", sm: "block" } }} />

        <Grid item xs={12} sm={4}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Tabs
              value={value}
              onChange={(_e, next: PaViewMode | null) => {
                if (next) onChange(next);
              }}
              centered
            >
              <Tab
                value="professional"
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Person sx={{ mr: 1 }} />
                    Visão Profissional
                  </Box>
                }
              />
              <Tab
                value="clinic"
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <AdminPanelSettings sx={{ mr: 1 }} />
                    Visão Clínica
                  </Box>
                }
              />
            </Tabs>
          </Box>
        </Grid>

        {/* Espaço à direita para manter tabs centralizadas */}
        <Grid
          item
          xs={false}
          sm={4}
          sx={{ display: { xs: "none", sm: "block" } }}
        />
      </Grid>
    </Paper>
  );
}

export function ProntoAtendimentoPage() {
  const { availableRoles } = useViewContext();
  const { contractProntoAtendimento, loading: contractLoading } =
    useProntoAtendimentoContractContext();

  const isPractitioner = availableRoles?.includes("practitioner");
  const canProfessional =
    Boolean(isPractitioner) && Boolean(contractProntoAtendimento);
  const canClinic =
    Boolean(contractProntoAtendimento) &&
    (availableRoles?.includes("manager") ||
      availableRoles?.includes("owner") ||
      availableRoles?.includes("associated"));

  const defaultView = useMemo<PaViewMode>(() => {
    if (canProfessional) return "professional";
    if (canClinic) return "clinic";
    return "professional";
  }, [canProfessional, canClinic]);

  const [paView, setPaView] = useState<PaViewMode>(defaultView);

  useEffect(() => {
    setPaView(defaultView);
  }, [defaultView]);

  const showToggle = canProfessional && canClinic;

  const pageBody = (content: ReactNode) => (
    <LargeContentBox>
      <Box sx={{ width: "100%", maxWidth: "100%", justifySelf: "stretch" }}>
        <PageHeader title="Pronto Atendimento" icon={MedicalServices} />
        {/* Seletor fora do action do header — centralizado como no dashboard */}
        {showToggle ? (
          <Box sx={{ mt: 1 }}>
            <PaViewTabs value={paView} onChange={setPaView} />
          </Box>
        ) : null}
        <Stack sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", maxWidth: "100%", p: 2 }}>{content}</Box>
        </Stack>
      </Box>
    </LargeContentBox>
  );

  if (contractLoading) {
    return pageBody(
      <Stack alignItems="center" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Stack>,
    );
  }

  if (!contractProntoAtendimento) {
    return pageBody(
      <Stack spacing={2}>
        <Typography variant="body1" color="text.secondary">
          Ative a extensão Pronto Atendimento para usar esta área.
        </Typography>
        <Button
          component={Link}
          to="/extensions"
          variant="contained"
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          Ver extensões
        </Button>
      </Stack>,
    );
  }

  if (!canProfessional && !canClinic) {
    return pageBody(
      <Typography variant="body1" color="text.secondary">
        Você não tem permissão para acessar o Pronto Atendimento nesta clínica.
      </Typography>,
    );
  }

  const panel =
    paView === "professional" && canProfessional ? (
      <ScheduleProntoAtendimentoPanel isActive hideTitle />
    ) : paView === "clinic" && canClinic ? (
      <ScheduleProntoAtendimentoClinicPanel isActive hideTitle />
    ) : canProfessional ? (
      <ScheduleProntoAtendimentoPanel isActive hideTitle />
    ) : (
      <ScheduleProntoAtendimentoClinicPanel isActive hideTitle />
    );

  return pageBody(panel);
}
